/**
 * ONE QUEUE, WORN BY EVERY CARD.
 *
 *   "Under each card should be the same queue labelling as at the top i.e.
 *    Waiting, Approved/Resolved, Rejected (with a reason), All."
 *
 * "The same" is the requirement, so this is one component rather than a
 * convention each card follows. A Compliance officer moving to Legal & Disputes
 * should not have to learn a second vocabulary for the same four states, and a
 * pattern repeated by hand is a pattern that diverges the third time somebody
 * builds a card in a hurry.
 *
 * WHY THE SECOND LABEL CHANGES AND THE OTHERS DO NOT
 *
 * The founder wrote "Approved/Resolved" — one state, two words, because what
 * finishing means depends on the card. An application is APPROVED; a dispute is
 * RESOLVED; a regulatory request is ANSWERED. So the settled label is supplied
 * per card and the other three are fixed, which keeps the shape identical
 * everywhere while letting each card use the word its people actually say.
 *
 * REJECTED ALWAYS CARRIES ITS REASON
 *
 *   "Rejected (with a reason)"
 *
 * Not decoration. A rejection nobody can explain later is indistinguishable
 * from an arbitrary one, and these are decisions a person can be asked to
 * account for in a dispute or to a regulator. So the Rejected list refuses to
 * render a row without one — it shows the row and says the reason is missing,
 * which is a defect somebody should see rather than a blank somebody skims.
 *
 * SEARCH IS BY CODE, NEVER BY NAME
 *
 *   "the staff member should be able to search using OFX and OFXB codes, date
 *    of submission, or approval status."
 *
 * Which is also prohibition 6: no name-based search for an individual member,
 * anywhere, under any framing. Individuals are reachable by exact OFX Connect
 * Code and nothing else. This component therefore offers a code box, a date
 * range and the status tabs, and has no name field to add later.
 */

import { useMemo, useState } from 'react';
import { O, SANS, Card, StatusPill, Field, textInputStyle } from './ui';

export const QUEUE_STATES = ['waiting', 'settled', 'rejected', 'all'];

/**
 * An OFX or OFXB code, as somebody types it.
 *
 * Case and spacing are forgiven; the code itself is not guessed at. Prohibition
 * 7 makes a code permanent once issued, so an exact match is a safe thing to
 * demand — there is no second code for the same person to miss.
 */
export const normaliseCode = (raw) =>
  String(raw || '').trim().toUpperCase().replace(/\s+/g, '');

/** True when this row matches the code somebody typed. Exact, on either code. */
export const matchesCode = (row, typed) => {
  const wanted = normaliseCode(typed);
  if (!wanted) return true;
  return [row.ofx_code, row.ofxb_code, row.connect_code, row.business_code]
    .filter(Boolean)
    .some((code) => normaliseCode(code) === wanted
      // A partial code is a legitimate way to find something you half-remember
      // from a phone call. It is still a CODE search: no name is involved, and
      // nothing about a person is discoverable without holding part of theirs.
      || normaliseCode(code).includes(wanted));
};

export const withinDates = (row, from, to) => {
  if (!from && !to) return true;
  const at = row.submitted_at || row.created_at || row.opened_at;
  if (!at) return false;
  const when = new Date(at);
  if (from && when < new Date(from)) return false;
  // The `to` date is inclusive of its whole day. Somebody searching "up to the
  // 14th" means the end of the 14th, and an exclusive bound silently hides
  // everything submitted that day.
  if (to) {
    const end = new Date(to);
    end.setHours(23, 59, 59, 999);
    if (when > end) return false;
  }
  return true;
};

const TABS = (settledLabel) => [
  ['waiting',  'Waiting'],
  ['settled',  settledLabel],
  ['rejected', 'Rejected'],
  ['all',      'All'],
];

export default function Queue({
  rows = [],
  // 'Approved' for an application, 'Resolved' for a dispute, 'Answered' for a
  // regulatory request. The state is the same; the word is the card's.
  settledLabel = 'Approved',
  // Maps a row to one of the four. Supplied per card because only the card
  // knows which of its columns carries the decision.
  stateOf,
  renderRow,
  empty = 'Nothing here.',
}) {
  const [tab, setTab] = useState('waiting');
  const [code, setCode] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const counts = useMemo(() => {
    const seed = { waiting: 0, settled: 0, rejected: 0, all: rows.length };
    return rows.reduce((acc, row) => {
      const state = stateOf(row);
      if (acc[state] !== undefined) acc[state] += 1;
      return acc;
    }, seed);
  }, [rows, stateOf]);

  const visible = useMemo(() => rows.filter((row) => {
    if (tab !== 'all' && stateOf(row) !== tab) return false;
    if (!matchesCode(row, code)) return false;
    if (!withinDates(row, from, to)) return false;
    return true;
  }), [rows, tab, code, from, to, stateOf]);

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        {TABS(settledLabel).map(([key, label]) => {
          const on = tab === key;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              aria-pressed={on}
              style={{
                padding: '9px 15px', borderRadius: 999,
                background: on ? O.green : 'transparent',
                border: `1px solid ${on ? O.green : O.hairlineOn}`,
                color: on ? '#FFFFFF' : O.inkMuted,
                font: 'inherit', fontFamily: SANS, fontSize: 12.5,
                fontWeight: on ? 700 : 600, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 8,
              }}
            >
              {label}
              <span style={{
                fontSize: 11, fontWeight: 700,
                color: on ? 'rgba(255,255,255,0.75)' : O.inkFaint,
              }}>{counts[key]}</span>
            </button>
          );
        })}
      </div>

      <Card style={{ marginBottom: 14, padding: 15 }}>
        <div style={{
          display: 'grid', gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
        }}>
          {/* No name field, and there will not be one. Prohibition 6: an
              individual is searchable by exact OFX Connect Code and nothing
              else, under any framing. */}
          <Field label="OFX or OFXB code" htmlFor="queue-code">
            <input id="queue-code" value={code} style={textInputStyle(false)}
                   autoComplete="off" placeholder="OFX-…"
                   onChange={(e) => setCode(e.target.value)} />
          </Field>
          <Field label="Submitted from" htmlFor="queue-from">
            <input id="queue-from" type="date" value={from} style={textInputStyle(false)}
                   onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="Submitted to" htmlFor="queue-to">
            <input id="queue-to" type="date" value={to} style={textInputStyle(false)}
                   onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
      </Card>

      {visible.length === 0 ? (
        <Card><p style={{ fontSize: 13, color: O.inkMuted, margin: 0 }}>
          {code || from || to
            ? 'Nothing matches that search. The code must be exact or a fragment of one — there is no search by name anywhere on Orchid.'
            : empty}
        </p></Card>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {visible.map((row) => renderRow(row, stateOf(row)))}
        </div>
      )}
    </div>
  );
}

/**
 * The rejection reason, or a visible hole where one should be.
 *
 * Rendered by the cards rather than assumed, so "Rejected (with a reason)" is
 * a property of every rejection list on the portal and not of the one card
 * whose author remembered.
 */
export const RejectionReason = ({ reason }) => {
  if (reason && String(reason).trim()) {
    return (
      <p style={{ margin: '6px 0 0', fontSize: 12, lineHeight: 1.6, color: O.inkMuted }}>
        <strong style={{ color: O.ink }}>Reason: </strong>{reason}
      </p>
    );
  }
  // Shown, not hidden. A rejection nobody can explain later is indistinguishable
  // from an arbitrary one, and this is a decision somebody can be asked to
  // account for.
  return (
    <p style={{ margin: '6px 0 0', fontSize: 12, lineHeight: 1.6, color: O.critical }}>
      This rejection carries no recorded reason.
    </p>
  );
};

export const StateLabel = ({ state, settledLabel = 'Approved' }) => {
  const tones = { waiting: 'quiet', settled: 'green', rejected: 'critical' };
  const labels = { waiting: 'Waiting', settled: settledLabel, rejected: 'Rejected' };
  return <StatusPill tone={tones[state] || 'quiet'}>{labels[state] || state}</StatusPill>;
};
